import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TablePagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  FilterList,
  Folder,
  Download,
  Person,
  Group,
  Settings,
  Visibility,
  Edit as EditIcon,
  ManageAccounts,
} from '@mui/icons-material';
import rbacApi from '../../api/rbacApi';
import { dsVar, ink } from '../../theme/tokens';

// Simple component to show project permissions in dialog
const ProjectPermissionsView: React.FC<{ project: any }> = ({ project }) => {
  const [groupUsers, setGroupUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [projectOwner, setProjectOwner] = useState<string>('');
  const [userRole, setUserRole] = useState<string>('');
  const [canManagePermissions, setCanManagePermissions] = useState<boolean>(false);

  // Load user role and check permissions
  const loadUserPermissions = useCallback(async () => {
    try {
      const userEmail = localStorage.getItem('user_email') || '';
      const response = await rbacApi.getCurrentUser(userEmail || null as any);
      
      let role = '';
      if (response && response.user && response.user.role) {
        role = response.user.role;
      } else if (response && response.role) {
        role = response.role;
      } else if (response && response.userRole) {
        role = response.userRole;
      } else {
        role = localStorage.getItem('user_role') || '';
      }
      
      setUserRole(role);
      
      // Check if user can manage permissions
      const isOrgAdmin = role === 'organisation_admin' || role === 'ADMIN';
      const isGroupAdmin = role === 'user_group_admin';
      const isProjectAdmin = projectOwner === userEmail;
      
      setCanManagePermissions(isOrgAdmin || isGroupAdmin || isProjectAdmin);
      
    } catch (err) {
      console.error('Error loading user permissions:', err);
      setCanManagePermissions(false);
    }
  }, [projectOwner]);

  const loadGroupUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Set project owner
      const owner = project.owner || project.createdBy || project.lastUpdatedByUser || '';
      setProjectOwner(owner);
      
      // Get all groups associated with this project
      const groups = await rbacApi.getGroups({ page: 1, limit: 1000 });
      const allGroups = Array.isArray(groups) ? groups : (groups?.userGroups || []);
      
      // Find groups that have this project
      const associatedGroups = allGroups.filter((group: any) => 
        group && group.projects && group.projects.includes(project.id)
      );
      
      // Collect all unique users from associated groups
      const allUsers = new Map();
      associatedGroups.forEach((group: any) => {
        if (group.members && Array.isArray(group.members)) {
          group.members.forEach((member: any) => {
            const email = typeof member === 'string' ? member : (member.email || member.userId || member.userEmail);
            if (email && !allUsers.has(email)) {
              const isOwner = email === owner;
              allUsers.set(email, {
                email,
                name: email.split('@')[0], // Use email prefix as name
                groupName: group.name,
                canEdit: isOwner, // Owner has edit access by default
                isAdmin: isOwner, // Owner is admin by default
                isOwner: isOwner,
                canView: true // Users from associated groups can view
              });
            }
          });
        }
      });
      
      // Load existing permissions for these users
      const userIds = Array.from(allUsers.keys());
      if (userIds.length > 0) {
        try {
          const organisationId = localStorage.getItem('org_id') || 'bctglobal';
          const permissions = await rbacApi.bulkReadProjectPermissions({ 
            projectId: project.id, 
            organisationId, 
            userIds 
          });
          
          // Update users with their existing permissions
          if (permissions && permissions.permissions) {
            permissions.permissions.forEach((perm: any) => {
              if (allUsers.has(perm.userId)) {
                const user = allUsers.get(perm.userId);
                // Only update permissions if user belongs to associated group
                user.canEdit = perm.permissions?.canEdit || user.isOwner;
                user.isAdmin = perm.permissions?.canManageUsers || user.isOwner;
                // Ensure canView is true for users from associated groups
                user.canView = true;
                allUsers.set(perm.userId, user);
              }
            });
          }
        } catch (permError) {
          console.error('Error loading existing permissions:', permError);
          // Continue with default permissions if API fails
        }
      }
      
      setGroupUsers(Array.from(allUsers.values()));
    } catch (err) {
      console.error('Error loading group users:', err);
      setError('Failed to load group users');
      setGroupUsers([]);
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => {
    if (project?.id) {
      loadUserPermissions();
      loadGroupUsers();
    }
  }, [project?.id, loadUserPermissions, loadGroupUsers]);

  const handleEditToggle = (userEmail: string) => {
    setGroupUsers(prev => 
      prev.map(user => 
        user.email === userEmail 
          ? { ...user, canEdit: !user.canEdit }
          : user
      )
    );
  };

  const handleAdminToggle = (userEmail: string) => {
    setGroupUsers(prev => 
      prev.map(user => 
        user.email === userEmail 
          ? { ...user, isAdmin: !user.isAdmin }
          : user
      )
    );
  };

  const handleSavePermissions = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const organisationId = localStorage.getItem('org_id') || 'bctglobal';
      const currentUserEmail = localStorage.getItem('user_email') || 'admin@example.com';
      
      // Validate that all users belong to groups associated with this project
      const groups = await rbacApi.getGroups({ page: 1, limit: 1000 });
      const allGroups = Array.isArray(groups) ? groups : (groups?.userGroups || []);
      const associatedGroups = allGroups.filter((group: any) => 
        group && group.projects && group.projects.includes(project.id)
      );
      
      // Get all valid user emails from associated groups
      const validUserEmails = new Set();
      associatedGroups.forEach((group: any) => {
        if (group.members && Array.isArray(group.members)) {
          group.members.forEach((member: any) => {
            const email = typeof member === 'string' ? member : (member.email || member.userId || member.userEmail);
            if (email) {
              validUserEmails.add(email);
            }
          });
        }
      });
      
      // Filter out users who don't belong to any associated group
      const validUsers = groupUsers.filter(user => validUserEmails.has(user.email));
      
      if (validUsers.length !== groupUsers.length) {
        const invalidUsers = groupUsers.filter(user => !validUserEmails.has(user.email));
        console.warn('Some users do not belong to groups associated with this project:', invalidUsers.map(u => u.email));
        setError(`Some users (${invalidUsers.map(u => u.email).join(', ')}) do not belong to groups associated with this project. They will not be granted permissions.`);
      }
      
      // Create assignments only for valid users
      const assignments = validUsers.map(user => ({
        userId: user.email,
        permissions: { 
          canView: true, // These users are from associated groups, so they can view
          canEdit: user.canEdit,
          canManageUsers: user.isAdmin
        }
      }));
      
      await rbacApi.batchUpdateProjectPermissions({ 
        projectId: project.id, 
        organisationId, 
        updatedBy: currentUserEmail, 
        assignments 
      });
      
      setSuccess(`Permissions updated successfully for ${validUsers.length} users!`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving permissions:', err);
      setError('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading group users...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box py={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ color: dsVar('fg') }}>
      {success && (
        <Alert severity="success" sx={{ mb: 3, bgcolor: 'rgba(16, 185, 129, 0.1)', color: ink('#10b981'), border: '1px solid rgba(16, 185, 129, 0.2)' }}>
          {success}
        </Alert>
      )}
      
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={2} mb={4}>
        <Typography variant="h6" sx={{ color: dsVar('accent-fg'), fontWeight: 700 }}>
          Project User Access
        </Typography>
        <Box display="flex" gap={1}>
          <Chip 
            label={`${groupUsers.filter(u => u.canEdit).length} Editors`}
            size="small" 
            sx={{ bgcolor: 'rgba(255, 222, 89, 0.1)', color: dsVar('accent-fg'), fontWeight: 600 }}
          />
          <Chip 
            label={`${groupUsers.filter(u => u.isAdmin).length} Admins`}
            size="small" 
            sx={{ bgcolor: 'rgba(16, 185, 129, 0.1)', color: ink('#10b981'), fontWeight: 600 }}
          />
        </Box>
        {!canManagePermissions && (
          <Chip 
            label="View Only" 
            size="small" 
            sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)', color: ink('#f59e0b'), fontWeight: 700, fontSize: '0.65rem' }}
          />
        )}
      </Box>
      
      {!canManagePermissions && (
        <Alert severity="info" sx={{ mb: 3, bgcolor: 'rgba(56, 189, 248, 0.1)', color: ink('#38bdf8'), border: '1px solid rgba(56, 189, 248, 0.2)' }}>
          Read-only access: Permissions can only be managed by Admins or Project Owners.
        </Alert>
      )}

      {groupUsers.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center', bgcolor: dsVar('page'), borderRadius: '12px', border: `1px dashed ${dsVar('line')}` }}>
          <Typography sx={{ color: dsVar('fg-2') }}>No users found in associated groups</Typography>
        </Box>
      ) : (
        <Box>
          <TableContainer component={Paper} sx={{ bgcolor: dsVar('page'), border: `1px solid ${dsVar('line')}`, borderRadius: '12px', backgroundImage: 'none', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: dsVar('line', 0.5) }}>
                <TableRow>
                  <TableCell sx={{ color: dsVar('fg-2'), fontWeight: 700, borderBottom: `1px solid ${dsVar('line')}` }}>User</TableCell>
                  <TableCell sx={{ color: dsVar('fg-2'), fontWeight: 700, borderBottom: `1px solid ${dsVar('line')}` }}>Group</TableCell>
                  <TableCell sx={{ color: dsVar('fg-2'), fontWeight: 700, borderBottom: `1px solid ${dsVar('line')}` }}>Current Role</TableCell>
                  <TableCell align="center" sx={{ color: dsVar('fg-2'), fontWeight: 700, borderBottom: `1px solid ${dsVar('line')}` }}>Edit Access</TableCell>
                  <TableCell align="center" sx={{ color: dsVar('fg-2'), fontWeight: 700, borderBottom: `1px solid ${dsVar('line')}` }}>Admin</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupUsers.map((user, index) => (
                  <TableRow key={index} sx={{ '& td': { borderBottom: `1px solid ${dsVar('line')}` } }}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: dsVar('line'), color: dsVar('accent-fg') }}>
                          {user.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ color: dsVar('fg'), fontWeight: 600 }}>{user.name}</Typography>
                          <Typography variant="caption" sx={{ color: dsVar('fg-2'), display: 'block' }}>{user.email}</Typography>
                          {user.isOwner && (
                            <Chip label="OWNER" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: dsVar('accent'), color: dsVar('on-accent'), fontWeight: 800, mt: 0.5 }} />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: dsVar('fg-2') }}>{user.groupName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.isAdmin ? "Admin" : (user.canEdit ? "Editor" : "Viewer")}
                        size="small" 
                        sx={{ 
                          height: 20,
                          fontSize: '0.65rem',
                          bgcolor: user.isAdmin ? 'rgba(16, 185, 129, 0.1)' : (user.canEdit ? 'rgba(255, 222, 89, 0.1)' : dsVar('fg-2', 0.1)),
                          color: user.isAdmin ? ink('#10b981') : (user.canEdit ? dsVar('accent-fg') : dsVar('fg-2')),
                          border: '1px solid currentColor'
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox 
                        checked={user.canEdit}
                        onChange={() => handleEditToggle(user.email)}
                        disabled={saving || !canManagePermissions || user.isOwner}
                        sx={{ color: dsVar('fg-faint'), '&.Mui-checked': { color: dsVar('accent-fg') } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox 
                        checked={user.isAdmin}
                        onChange={() => handleAdminToggle(user.email)}
                        disabled={saving || !canManagePermissions || user.isOwner}
                        sx={{ color: dsVar('fg-faint'), '&.Mui-checked': { color: ink('#10b981') } }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {canManagePermissions && (
            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button
                variant="contained"
                onClick={handleSavePermissions}
                disabled={saving}
                sx={{ 
                  bgcolor: dsVar('accent'), 
                  color: dsVar('on-accent'), 
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 4,
                  '&:hover': { bgcolor: dsVar('accent') },
                  '&.Mui-disabled': { bgcolor: dsVar('line'), color: dsVar('fg-2') }
                }}
              >
                {saving ? <CircularProgress size={20} sx={{ color: dsVar('on-accent') }} /> : 'Save Permissions'}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};



// Mock data for Projects Management
const mockProjects = [
  {
    id: 'project1',
    name: 'Project Alpha',
    description: 'Core product development project',
    userGroup: 'Engineering Team',
    owner: 'Unknown',
    memberCount: 6,
    status: 'active',
    createdAt: '2024-01-01',
    lastActivity: '2024-01-15',
    type: 'Development',
    priority: 'High',
    permissions: {
      canAddCrossGroupMembers: true,
      editors: [],
      viewers: []
    }
  },
  {
    id: 'project2',
    name: 'Project Beta',
    description: 'Marketing campaign project',
    userGroup: 'Marketing Team',
    owner: 'Unknown',
    memberCount: 4,
    status: 'active',
    createdAt: '2024-01-02',
    lastActivity: '2024-01-14',
    type: 'Marketing',
    priority: 'Medium',
    permissions: {
      canAddCrossGroupMembers: false,
      editors: [],
      viewers: []
    }
  },
  {
    id: 'project3',
    name: 'Project Gamma',
    description: 'API integration project',
    userGroup: 'Engineering Team',
    owner: 'Unknown',
    memberCount: 3,
    status: 'active',
    createdAt: '2024-01-03',
    lastActivity: '2024-01-13',
    type: 'Integration',
    priority: 'High',
    permissions: {
      canAddCrossGroupMembers: true,
      editors: [],
      viewers: []
    }
  },
  {
    id: 'project4',
    name: 'Project Delta',
    description: 'Customer support portal',
    userGroup: 'Support Team',
    owner: 'David Brown',
    memberCount: 5,
    status: 'inactive',
    createdAt: '2024-01-04',
    lastActivity: '2024-01-10',
    type: 'Support',
    priority: 'Low',
    permissions: {
      canAddCrossGroupMembers: false,
      editors: [],
      viewers: ['Lisa Chen', 'Alex Kim', 'Emma Davis']
    }
  },
  {
    id: 'project5',
    name: 'Project Epsilon',
    description: 'Sales automation system',
    userGroup: 'Sales Team',
    owner: 'Sarah Wilson',
    memberCount: 4,
    status: 'active',
    createdAt: '2024-01-05',
    lastActivity: '2024-01-12',
    type: 'Sales',
    priority: 'Medium',
    permissions: {
      canAddCrossGroupMembers: true,
      editors: ['Mike Johnson'],
      viewers: ['David Brown', 'Lisa Chen']
    }
  }
];

interface ProjectsManagementProps {
  canManageProjects?: boolean;
  canDeleteProjects?: boolean;
  canCrossGroupAccess?: boolean;
}

const ProjectsManagement: React.FC<ProjectsManagementProps> = ({ 
  canManageProjects = true, 
  canDeleteProjects = true, 
  canCrossGroupAccess = true 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedProjectForPermissions, setSelectedProjectForPermissions] = useState<any>(null);
  const [groups, setGroups] = useState<any[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalProjects, setTotalProjects] = useState(0);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, project: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const handleOpenPermissions = (project: any) => {
    setSelectedProjectForPermissions(project);
    setPermissionsDialogOpen(true);
  };

  const handleClosePermissions = () => {
    setPermissionsDialogOpen(false);
    setSelectedProjectForPermissions(null);
  };


  const loadGroups = async () => {
    try {
      // Try to get all groups with their projects first
      let response = await rbacApi.getAllGroupsWithProjects();
      
      if (response && response.groupProjects) {
        // Use the new endpoint that includes projects
        const groupsArray = response.groupProjects.map((groupProject: any) => ({
          id: groupProject.groupId,
          name: groupProject.groupName,
          projects: groupProject.projects.map((project: any) => project._id || project.id)
        }));
        setGroups(groupsArray);
        console.log(`Loaded ${groupsArray.length} groups with projects for project management`);
      } else {
        // Fallback to regular groups endpoint
        response = await rbacApi.getGroups({ 
          page: 1, 
          limit: 1000
        });
        const groupsArray = Array.isArray(response) ? response : (response?.userGroups || []);
        setGroups(groupsArray);
        console.log(`Loaded ${groupsArray.length} groups for project management (fallback)`);
      }
    } catch (err) {
      console.error('Error loading groups:', err);
      setGroups([]);
    }
  };

  // Get groups associated with a specific project
  const getProjectGroups = (projectId: string) => {
    const associatedGroups = groups.filter(group => 
      group && group.projects && group.projects.includes(projectId)
    );
    console.log(`Project ${projectId} has ${associatedGroups.length} associated groups:`, 
      associatedGroups.map(g => g.name));
    return associatedGroups;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'archived':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High':
        return 'error';
      case 'Medium':
        return 'warning';
      case 'Low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Development':
        return 'primary';
      case 'Marketing':
        return 'secondary';
      case 'Integration':
        return 'info';
      case 'Support':
        return 'success';
      case 'Sales':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Load projects from backend without filtering (filtering done on frontend)
  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Call API without search/filter parameters - get all projects
      const response = await rbacApi.getProjects({ 
        page: 1, 
        limit: 1000 // Get all projects for client-side filtering
      });
      
      if (response && response.projects && Array.isArray(response.projects)) {
        // Real data from backend with pagination
        const mappedProjects = response.projects.map((project: any) => {
          // Dynamic field mapping - handle different possible field names
          const projectId = project._id || project.id || project.projectId || project.project_id;
          const projectName = project.projectName || project.name || project.title || project.project_name;
          const projectDescription = project.projectDescription || project.description || project.desc || project.project_description;
          const projectOwner = project.lastUpdatedByUser || project.owner || project.createdBy || project.created_by || project.userId;
          const projectStatus = project.active !== undefined ? (project.active ? 'active' : 'inactive') : 
                               (project.status || project.projectStatus || 'inactive');
          const projectType = project.projectType || project.type || project.category || project.project_type;
          const projectLanguage = project.language || project.lang || project.language_code || 'EN';
          const projectGeography = project.geography || project.geo || project.country || project.region || 'US';
          const projectLastUpdated = project.lastUpdatedDate || project.updatedAt || project.updated_at || project.modifiedAt;
          const projectConfig = project.configuration || project.config || project.settings || {};
          
          return {
            id: projectId,
            name: projectName || 'Unknown Project',
            description: projectDescription || 'No description available',
            owner: projectOwner || 'Unknown',
            status: projectStatus,
            projectType: projectType || 'Unknown',
            language: projectLanguage,
            geography: projectGeography,
            lastUpdated: projectLastUpdated,
            configuration: projectConfig,
            permissions: {
              canAddCrossGroupMembers: true,
              editors: [],
              viewers: []
            }
          };
        });
        
        setProjects(mappedProjects);
        setTotalProjects(mappedProjects.length);
      } else if (response && Array.isArray(response)) {
        // Fallback for array response (no pagination)
        const mappedProjects = response.map((project: any) => {
          const projectId = project._id || project.id || project.projectId || project.project_id;
          const projectName = project.projectName || project.name || project.title || project.project_name;
          const projectDescription = project.projectDescription || project.description || project.desc || project.project_description;
          const projectOwner = project.lastUpdatedByUser || project.owner || project.createdBy || project.created_by || project.userId;
          const projectStatus = project.active !== undefined ? (project.active ? 'active' : 'inactive') : 
                               (project.status || project.projectStatus || 'inactive');
          const projectType = project.projectType || project.type || project.category || project.project_type;
          const projectLanguage = project.language || project.lang || project.language_code || 'EN';
          const projectGeography = project.geography || project.geo || project.country || project.region || 'US';
          const projectLastUpdated = project.lastUpdatedDate || project.updatedAt || project.updated_at || project.modifiedAt;
          const projectConfig = project.configuration || project.config || project.settings || {};
          
          return {
            id: projectId,
            name: projectName || 'Unknown Project',
            description: projectDescription || 'No description available',
            owner: projectOwner || 'Unknown',
            status: projectStatus,
            projectType: projectType || 'Unknown',
            language: projectLanguage,
            geography: projectGeography,
            lastUpdated: projectLastUpdated,
            configuration: projectConfig,
            permissions: {
              canAddCrossGroupMembers: true,
              editors: [],
              viewers: []
            }
          };
        });
        
        setProjects(mappedProjects);
        setTotalProjects(mappedProjects.length);
      } else {
        // Fallback to mock data
        setProjects(mockProjects);
        setTotalProjects(mockProjects.length);
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load projects. Using mock data.');
      setProjects(mockProjects);
      setTotalProjects(mockProjects.length);
    } finally {
      setLoading(false);
    }
  };

  // Load projects on component mount only
  useEffect(() => {
    loadProjects();
  }, []);

  // Load groups on component mount
  useEffect(() => {
    loadGroups();
  }, []);

  // Log complete project-group mapping when both projects and groups are loaded
  useEffect(() => {
    if (projects.length > 0 && groups.length > 0) {
      console.log('📊 Complete Project-Group Mapping:');
      projects.forEach(project => {
        const projectGroups = getProjectGroups(project.id);
        console.log(`  ${project.name}: [${projectGroups.map(g => g.name).join(', ')}]`);
      });
    }
  }, [projects, groups]);

  // Reset to first page when search term or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  // Handle filter reset
  const handleFilterReset = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    const nextPage = newPage + 1; // MUI TablePagination uses 0-based indexing
    if (nextPage !== currentPage) {
      setCurrentPage(nextPage);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1); // Reset to first page when changing page size
    }
  };

  // Client-side filtering
  const filteredProjects = projects.filter(project => {
    // Apply search filter
    const matchesSearch = !searchTerm || 
      project.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.owner?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.projectType?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Apply status filter
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Client-side pagination
  const totalFilteredProjects = filteredProjects.length;
  const totalFilteredPages = Math.ceil(totalFilteredProjects / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedProjects = filteredProjects.slice(startIndex, endIndex);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: dsVar('page'), minHeight: '100vh', color: dsVar('fg') }}>
      {/* Search and Filters Section */}
      <Card sx={{ 
        bgcolor: dsVar('surface'), 
        border: `1px solid ${dsVar('line')}`, 
        borderRadius: '16px', 
        mb: 4, 
        backgroundImage: 'none',
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: dsVar('fg'), mb: 1, letterSpacing: '-0.02em' }}>
                Projects ({totalFilteredProjects})
              </Typography>
              {groups.length > 0 && (
                <Typography variant="body2" sx={{ color: dsVar('fg-2') }}>
                  Managing project-group associations across {groups.length} active groups.
                </Typography>
              )}
            </Box>
            <Box display="flex" gap={2} flexGrow={{ xs: 1, md: 0 }}>
              <TextField
                size="small"
                placeholder="Search projects, owners..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: dsVar('accent-fg') }} />
                    </InputAdornment>
                  ),
                  sx: {
                    color: dsVar('fg'),
                    bgcolor: dsVar('page'),
                    borderRadius: '12px',
                    '& fieldset': { borderColor: dsVar('line') },
                    '&:hover fieldset': { borderColor: dsVar('accent-fg') },
                    '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                  }
                }}
                sx={{ minWidth: { xs: '100%', md: 300 } }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ color: dsVar('fg-2'), '&.Mui-focused': { color: dsVar('accent-fg') } }}>Filter Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Filter Status"
                  sx={{
                    color: dsVar('fg'),
                    bgcolor: dsVar('page'),
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('accent-fg') },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('accent-fg') },
                    '& .MuiSvgIcon-root': { color: dsVar('fg-2') }
                  }}
                  MenuProps={{
                    PaperProps: { sx: { bgcolor: dsVar('surface'), color: dsVar('fg'), border: `1px solid ${dsVar('line')}` } }
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="planned">Planned</MenuItem>
                </Select>
              </FormControl>
              {(searchTerm || filterStatus !== 'all') && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleFilterReset}
                  sx={{ 
                    textTransform: 'none', 
                    borderRadius: '10px',
                    borderColor: dsVar('line'),
                    color: dsVar('fg-2'),
                    px: 3,
                    '&:hover': { borderColor: dsVar('accent-fg'), color: dsVar('accent-fg'), bgcolor: 'rgba(255, 222, 89, 0.05)' }
                  }}
                >
                  Reset
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Projects Table Section */}
      <TableContainer component={Paper} sx={{ 
        bgcolor: dsVar('surface'), 
        border: `1px solid ${dsVar('line')}`, 
        borderRadius: '20px', 
        backgroundImage: 'none', 
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
      }}>
        <Table>
          <TableHead sx={{ bgcolor: dsVar('line', 0.5) }}>
            <TableRow>
              <TableCell sx={{ color: dsVar('accent-fg'), fontWeight: 700, py: 3, borderBottom: `1px solid ${dsVar('line')}` }}>Project Metadata</TableCell>
              <TableCell sx={{ color: dsVar('accent-fg'), fontWeight: 700, py: 3, borderBottom: `1px solid ${dsVar('line')}` }}>Project Owner</TableCell>
              <TableCell sx={{ color: dsVar('accent-fg'), fontWeight: 700, py: 3, borderBottom: `1px solid ${dsVar('line')}` }}>Assigned Groups</TableCell>
              <TableCell align="right" sx={{ color: dsVar('accent-fg'), fontWeight: 700, py: 3, borderBottom: `1px solid ${dsVar('line')}` }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                  <CircularProgress size={40} sx={{ color: dsVar('accent-fg'), mb: 2 }} />
                  <Typography sx={{ color: dsVar('fg-2') }}>Aggregating project data...</Typography>
                </TableCell>
              </TableRow>
            ) : paginatedProjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                  <Typography sx={{ color: dsVar('fg-2'), fontSize: '1.1rem' }}>
                    {searchTerm || filterStatus !== 'all' 
                      ? 'No projects matching your search criteria were found.' 
                      : 'You do not have any projects assigned to your organization.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedProjects.map((project) => (
                <TableRow key={project.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255, 222, 89, 0.02)' }, '& td': { borderBottom: `1px solid ${dsVar('line')}`, py: 3 } }}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2.5}>
                      <Avatar sx={{ bgcolor: dsVar('line'), border: `1px solid ${dsVar('accent-fg')}`, width: 44, height: 44, color: dsVar('accent-fg') }}>
                        <Folder sx={{ fontSize: '1.4rem' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ color: dsVar('fg'), fontWeight: 700, mb: 0.5 }}>
                          {project.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: dsVar('fg-2'), lineHeight: 1.4, maxWidth: 350 }}>
                          {project.description}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ width: 30, height: 30, fontSize: '0.8rem', bgcolor: 'rgba(255, 222, 89, 0.1)', color: dsVar('accent-fg'), fontWeight: 700, border: '1px solid rgba(255, 222, 89, 0.2)' }}>
                        {project.owner.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ color: dsVar('fg'), fontWeight: 600 }}>
                          {project.owner}
                        </Typography>
                        <Chip 
                          label={project.status.toUpperCase()} 
                          size="small" 
                          sx={{ 
                            height: 18, 
                            fontSize: '0.6rem', 
                            fontWeight: 800, 
                            mt: 0.5,
                            bgcolor: project.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                            color: project.status === 'active' ? ink('#10b981') : ink('#f59e0b'),
                            border: '1px solid currentColor'
                          }} 
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {getProjectGroups(project.id).length > 0 ? (
                        getProjectGroups(project.id).map((group) => (
                          <Chip
                            key={group.id}
                            label={group.name}
                            size="small"
                            sx={{ 
                              fontSize: '0.75rem', 
                              height: '26px',
                              bgcolor: 'rgba(56, 189, 248, 0.1)',
                              color: ink('#38bdf8'),
                              fontWeight: 600,
                              borderRadius: '8px',
                              border: '1px solid rgba(56, 189, 248, 0.2)',
                              '&:hover': { bgcolor: 'rgba(56, 189, 248, 0.2)' }
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="caption" sx={{ color: dsVar('fg-2'), fontStyle: 'italic' }}>
                          No groups assigned
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<ManageAccounts sx={{ fontSize: '1.2rem' }} />}
                      onClick={() => handleOpenPermissions(project)}
                      sx={{ 
                        textTransform: 'none',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        bgcolor: dsVar('accent'),
                        color: dsVar('on-accent'),
                        borderRadius: '10px',
                        px: 2.5,
                        py: 1,
                        '&:hover': { bgcolor: dsVar('accent'), transform: 'translateY(-1px)' },
                        transition: 'all 0.2s',
                        boxShadow: '0 4px 12px rgba(255, 222, 89, 0.15)'
                      }}
                    >
                      Manage Access
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        {/* Pagination Section */}
        {!loading && totalFilteredProjects > 0 && (
          <Box display="flex" justifyContent="space-between" alignItems="center" p={3} sx={{ bgcolor: dsVar('line', 0.3), borderTop: `1px solid ${dsVar('line')}` }}>
            <Typography variant="body2" sx={{ color: dsVar('fg-2') }}>
              Showing <strong>{startIndex + 1}</strong> to <strong>{Math.min(endIndex, totalFilteredProjects)}</strong> of <strong>{totalFilteredProjects}</strong> projects
            </Typography>
            <TablePagination
              component="div"
              count={totalFilteredProjects}
              page={currentPage - 1}
              onPageChange={handlePageChange}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handlePageSizeChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ 
                color: dsVar('fg-2'),
                '& .MuiTablePagination-selectIcon': { color: dsVar('accent-fg') },
                '& .MuiIconButton-root': { color: dsVar('accent-fg') },
                '& .MuiIconButton-root.Mui-disabled': { color: dsVar('fg-faint') }
              }}
            />
          </Box>
        )}
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: dsVar('surface'),
            color: dsVar('fg'),
            borderRadius: '16px',
            border: `1px solid ${dsVar('line')}`,
            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            minWidth: '220px',
            '& .MuiMenuItem-root': {
              py: 1.5,
              fontSize: '0.9rem',
              '&:hover': { bgcolor: 'rgba(255, 222, 89, 0.05)', color: dsVar('accent-fg') },
              '& .MuiListItemIcon-root': { color: 'inherit' }
            }
          }
        }}
      >
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><Visibility /></ListItemIcon>
          <ListItemText primary="View Project Details" />
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><EditIcon /></ListItemIcon>
          <ListItemText primary="Edit Configuration" />
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><Person /></ListItemIcon>
          <ListItemText primary="Direct Member Sync" />
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><Settings /></ListItemIcon>
          <ListItemText primary="Settings & Webhooks" />
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><Download /></ListItemIcon>
          <ListItemText primary="Export Audit Logs" />
        </MenuItem>
        <Divider sx={{ bgcolor: dsVar('line'), my: 1 }} />
        <MenuItem onClick={handleMenuClose} sx={{ color: '#ef4444 !important', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.05) !important' } }}>
          <ListItemIcon><Delete sx={{ color: 'inherit' }} /></ListItemIcon>
          <ListItemText primary="Archive Project" />
        </MenuItem>
      </Menu>

      {/* Permissions Management Dialog */}
      <Dialog
        open={permissionsDialogOpen}
        onClose={handleClosePermissions}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: dsVar('page'),
            backgroundImage: 'none',
            borderRadius: '24px',
            border: `1px solid ${dsVar('line')}`,
            color: dsVar('fg'),
            boxShadow: '0 24px 72px rgba(0,0,0,0.8)',
            minHeight: '650px'
          }
        }}
      >
        <DialogTitle sx={{ p: 4, pb: 2 }}>
          <Box display="flex" alignItems="center" gap={3}>
            <Box sx={{ p: 1.5, bgcolor: 'rgba(255, 222, 89, 0.1)', borderRadius: '14px' }}>
              <ManageAccounts sx={{ color: dsVar('accent-fg'), fontSize: '2rem' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: dsVar('fg') }}>
                Access Control Panel
              </Typography>
              <Typography variant="body1" sx={{ color: dsVar('accent-fg'), fontWeight: 600 }}>
                {selectedProjectForPermissions?.name || 'Loading Project...'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4, pt: 2 }}>
          {selectedProjectForPermissions && (
            <ProjectPermissionsView project={selectedProjectForPermissions} />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 4, pt: 0, gap: 2 }}>
          <Button 
            onClick={handleClosePermissions} 
            variant="outlined"
            sx={{ 
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              px: 4,
              py: 1.2,
              borderColor: dsVar('line'),
              color: dsVar('fg-2'),
              '&:hover': { borderColor: dsVar('accent-fg'), color: dsVar('accent-fg'), bgcolor: 'rgba(255, 222, 89, 0.05)' }
            }}
          >
            Finished
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ProjectsManagement;